import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [campaign, setCampaign] = useState({
    name: '',
    message_type: 'text',
    content: { text: '' },
    recipients: [],
    scheduled_at: '',
    template_id: ''
  });
  const [recipientsText, setRecipientsText] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchContacts();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const recipients = recipientsText
      .split('\n')
      .map(r => r.trim())
      .filter(r => r.length > 0);

    if (recipients.length === 0) {
      toast.error('Veuillez ajouter au moins un destinataire');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...campaign,
        recipients,
        scheduled_at: campaign.scheduled_at || null
      };

      const response = await axios.post(`${API_URL}/campaigns`, payload);
      toast.success('Campagne créée avec succès!');
      navigate(`/campaigns/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la campagne');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="create-campaign-page">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/campaigns')}
            className="rounded-lg"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nouvelle campagne
            </h1>
            <p className="mt-2 text-slate-600">Créez une nouvelle campagne d'envoi RCS</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="rounded-2xl border-slate-100">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Détails de la campagne</CardTitle>
              <CardDescription>Configurez les paramètres de votre campagne</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Campaign Name */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-slate-700">
                  Nom de la campagne *
                </label>
                <Input
                  id="name"
                  value={campaign.name}
                  onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                  placeholder="Ex: Promotion été 2024"
                  required
                  data-testid="campaign-name-input"
                  className="rounded-xl border-slate-200 h-12"
                />
              </div>

              {/* Message Type */}
              <div className="space-y-2">
                <label htmlFor="message-type" className="text-sm font-medium text-slate-700">
                  Type de message *
                </label>
                <Select
                  value={campaign.message_type}
                  onValueChange={(value) => setCampaign({ ...campaign, message_type: value })}
                >
                  <SelectTrigger id="message-type" className="rounded-xl h-12" data-testid="message-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texte simple</SelectItem>
                    <SelectItem value="media">Média (image/vidéo)</SelectItem>
                    <SelectItem value="rich_card">Carte enrichie</SelectItem>
                    <SelectItem value="carousel">Carrousel</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message Content */}
              <div className="space-y-2">
                <label htmlFor="content" className="text-sm font-medium text-slate-700">
                  Contenu du message *
                </label>
                <Textarea
                  id="content"
                  value={campaign.content.text || ''}
                  onChange={(e) => setCampaign({ ...campaign, content: { text: e.target.value } })}
                  placeholder="Saisissez votre message..."
                  rows={6}
                  required
                  data-testid="message-content-textarea"
                  className="rounded-xl border-slate-200 resize-none"
                />
                <p className="text-xs text-slate-500">{campaign.content.text?.length || 0} caractères</p>
              </div>

              {/* Recipients */}
              <div className="space-y-2">
                <label htmlFor="recipients" className="text-sm font-medium text-slate-700">
                  Destinataires (format E.164) *
                </label>
                <Textarea
                  id="recipients"
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="+33612345678\n+33687654321\n..."
                  rows={6}
                  required
                  data-testid="recipients-textarea"
                  className="rounded-xl border-slate-200 resize-none font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Un numéro par ligne. {recipientsText.split('\n').filter(r => r.trim()).length} destinataire(s)
                </p>
              </div>

              {/* Scheduled Date */}
              <div className="space-y-2">
                <label htmlFor="scheduled" className="text-sm font-medium text-slate-700">
                  Programmer l'envoi (optionnel)
                </label>
                <Input
                  id="scheduled"
                  type="datetime-local"
                  value={campaign.scheduled_at}
                  onChange={(e) => setCampaign({ ...campaign, scheduled_at: e.target.value })}
                  data-testid="scheduled-input"
                  className="rounded-xl border-slate-200 h-12"
                />
              </div>

              {/* Template Selection */}
              {templates.length > 0 && (
                <div className="space-y-2">
                  <label htmlFor="template" className="text-sm font-medium text-slate-700">
                    Utiliser un template (optionnel)
                  </label>
                  <Select
                    value={campaign.template_id}
                    onValueChange={(value) => setCampaign({ ...campaign, template_id: value })}
                  >
                    <SelectTrigger id="template" className="rounded-xl h-12" data-testid="template-select">
                      <SelectValue placeholder="Sélectionnez un template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/campaigns')}
                  className="rounded-full"
                  data-testid="cancel-button"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="rounded-full px-6 bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  data-testid="create-button"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {loading ? 'Création...' : 'Créer la campagne'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </Layout>
  );
};

export default CreateCampaign;